import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Spin } from 'antd';

import { COLORS } from '../utils/constants';
import { fluid } from '../utils/responsive';
import { getAllDeviceApprovalRequests, updateDeviceApprovalRequest, getDeviceApprovalRequestsCounts } from 'actions/userManagementV2';
import GridIcon from '../../../Assets/userManagementV3/gridIcon.svg';
import ListIcon from '../../../Assets/userManagementV3/listIcon.svg';
import { IMG_URL } from '../../../env.json';
import UserActionConfirmModal from '../../../pages/userManagementV3/localComponent/userActionConfirmModal';

// Same PATCH /user/device-approval-request and GET /user/device-approval-requests
// this list has always used (src/pages/userManagementV2/index.js's
// RegistrationRequestCard/UserRegistrationWrapper) — only the list/grid
// presentation below is new, matching StaffListTable.jsx's visual language.
const FILTER_CHIPS = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
];

const AVATAR_COLORS = ["#594099", "#1982C4", "#8AC926", "#F5A623", "#FF6B6B", "#4ECDC4", "#D97706", "#059669"];

const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
};

// Matches the "default.png" guard StaffProfilePage.js/StaffListTable.jsx use
// elsewhere — the backend hands back that filename (not null/empty) for a
// user with no real photo, which is otherwise a perfectly loadable image URL
// and would render as a generic silhouette instead of falling back to
// Avatar's colored initials.
const resolveImage = (image) => {
    if (!image || image === "default.png") return "";
    return image.startsWith("http") ? image : `${IMG_URL}/${image}`;
};

// GET /user/device-approval-requests record -> the row shape this table renders.
const mapRecordToRow = (record) => ({
    id: record?.id,
    name: record?.user?.name || "N/A",
    phone: record?.user?.phone || "",
    image: resolveImage(record?.user?.image),
    role: record?.role || "Student",
    oldDevice: [record?.currentDeviceMake, record?.currentDeviceModel].filter(Boolean).join(" ") || "N/A",
    newDevice: [record?.requestedDeviceMake, record?.requestedDeviceModel].filter(Boolean).join(" ") || "N/A",
    requestedAt: record?.createdAt,
    decidedAt: record?.updatedAt,
    approvalStatus: record?.approvalStatus || "pending",
});

// Backend is down right now, so there's nothing real to fetch — these stand
// in (already in the row shape mapRecordToRow produces) purely so the
// list/grid design can be checked visually. handleDecide below detects this
// fallback and updates it locally instead of PATCHing, so APPROVE/REJECT stay
// clickable in the meantime. Remove MOCK_ROWS (and the fallback below) once
// the real API is reachable again.
const MOCK_ROWS = [
    { id: "mock-1", name: "Ananya Rao", phone: "+919876543210", image: "", role: "Student", oldDevice: "Samsung Galaxy M31", newDevice: "OnePlus Nord CE3", requestedAt: "2026-08-23T10:15:00.000Z", decidedAt: null, approvalStatus: "pending" },
    { id: "mock-2", name: "Rahul Mehta", phone: "+919812345678", image: "", role: "Student", oldDevice: "Redmi Note 10", newDevice: "iPhone 13", requestedAt: "2026-08-23T14:40:00.000Z", decidedAt: null, approvalStatus: "pending" },
    { id: "mock-3", name: "Sneha Iyer", phone: "+919845098450", image: "", role: "Student", oldDevice: "Vivo Y20", newDevice: "Samsung Galaxy A54", requestedAt: "2026-08-24T06:05:00.000Z", decidedAt: null, approvalStatus: "pending" },
    { id: "mock-4", name: "Kabir Singh", phone: "+919900112233", image: "", role: "Student", oldDevice: "Realme 8", newDevice: "Realme 11 Pro", requestedAt: "2026-08-20T09:00:00.000Z", decidedAt: "2026-08-21T11:30:00.000Z", approvalStatus: "approved" },
    { id: "mock-5", name: "Meera Nair", phone: "+919871234560", image: "", role: "Student", oldDevice: "Oppo A16", newDevice: "Oppo Reno 8", requestedAt: "2026-08-19T13:20:00.000Z", decidedAt: "2026-08-20T08:10:00.000Z", approvalStatus: "approved" },
    { id: "mock-6", name: "Arjun Das", phone: "+919823456789", image: "", role: "Student", oldDevice: "Motorola G60", newDevice: "iPhone 12", requestedAt: "2026-08-18T16:45:00.000Z", decidedAt: "2026-08-19T09:15:00.000Z", approvalStatus: "rejected" },
];

const timeAgo = (value) => {
    if (!value) return "";
    const then = new Date(value).getTime();
    if (Number.isNaN(then)) return "";
    const diffMs = Date.now() - then;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

const Avatar = React.memo(({ name, image, size = 52 }) => {
    const color = AVATAR_COLORS[(name || "N").charCodeAt(0) % AVATAR_COLORS.length];
    if (image) {
        return (
            <img
                src={image}
                alt={name}
                style={{ width: fluid(size), height: fluid(size), borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            />
        );
    }
    return (
        <div style={{
            width: fluid(size),
            height: fluid(size),
            borderRadius: "50%",
            backgroundColor: `${color}1A`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: fluid(size * 0.38),
            fontWeight: 700,
            flexShrink: 0,
        }}>
            {getInitials(name)}
        </div>
    );
});
Avatar.displayName = 'Avatar';

const DeviceSwap = React.memo(({ oldDevice, newDevice }) => (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0, gap: fluid(2) }}>
        <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#222222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
            <span style={{ fontWeight: 600, color: "#191919" }}>Old device: </span>{oldDevice}
        </span>
        <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#222222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
            <span style={{ fontWeight: 600, color: "#191919" }}>New device: </span>{newDevice}
        </span>
    </div>
));
DeviceSwap.displayName = 'DeviceSwap';

const StatusBadge = React.memo(({ status }) => {
    const cfg = {
        approved: { bg: "#ECFDF5", border: "#34D399", color: "#059669", label: "Approved" },
        rejected: { bg: "#FEF2F2", border: "#FCA5A5", color: "#DC2626", label: "Rejected" },
        pending: { bg: "#FFFBEB", border: "#FBBF24", color: "#D97706", label: "Pending" },
    }[status] || { bg: "#F1F1F1", border: "#D9D9D9", color: "#636363", label: status };
    return (
        <span style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: fluid(20),
            padding: `0 ${fluid(6)}`,
            border: `1px solid ${cfg.border}`,
            borderRadius: fluid(4),
            fontFamily: "Roboto, sans-serif",
            fontSize: fluid(12),
            fontWeight: 600,
            backgroundColor: cfg.bg,
            color: cfg.color,
            whiteSpace: "nowrap",
        }}>
            {cfg.label}
        </span>
    );
});
StatusBadge.displayName = 'StatusBadge';

const ActionButtons = React.memo(({ row, filter, busy, onDecide }) => {
    if (filter === "approved") {
        return (
            <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(row.id, "rejected")}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: fluid(18),
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    color: "#FF414D",
                    opacity: busy ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontFamily: "Roboto, sans-serif",
                }}
            >
                CHANGE TO REJECT
            </button>
        );
    }
    if (filter === "rejected") {
        return (
            <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(row.id, "approved")}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: fluid(18),
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    color: "#00A566",
                    opacity: busy ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontFamily: "Roboto, sans-serif",
                }}
            >
                CHANGE TO APPROVE
            </button>
        );
    }
    return (
        <div style={{ display: "flex", alignItems: "center", gap: fluid(14) }}>
            <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(row.id, "rejected")}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: fluid(18),
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    color: "#FF414D",
                    opacity: busy ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontFamily: "Roboto, sans-serif",
                }}
            >
                REJECT
            </button>
            <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(row.id, "approved")}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: fluid(18),
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    color: "#00A566",
                    opacity: busy ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontFamily: "Roboto, sans-serif",
                }}
            >
                APPROVE
            </button>
        </div>
    );
});
ActionButtons.displayName = 'ActionButtons';

const COLUMN_TEMPLATE = `minmax(${fluid(240)}, 2fr) minmax(${fluid(110)}, 0.9fr) minmax(${fluid(280)}, 2.2fr) minmax(${fluid(110)}, 0.9fr) minmax(${fluid(200)}, 1.3fr)`;

const TableRow = React.memo(({ row, filter, busy, onDecide, isLast }) => (
    <div style={{ position: "relative" }}>
        <div style={{
            display: "grid",
            gridTemplateColumns: COLUMN_TEMPLATE,
            alignItems: "center",
            gap: fluid(16),
            minHeight: fluid(92),
            padding: `0 ${fluid(24)}`,
            boxSizing: "border-box",
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: fluid(14), minWidth: 0 }}>
                <Avatar name={row.name} image={row.image} size={52} />
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                        {row.name}
                    </span>
                    <span style={{ fontSize: "14px", lineHeight: "22px", fontWeight: 400, color: "#AEAEAE", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                        {row.phone?.replace(/^\+91/, "")}
                    </span>
                </div>
            </div>

            <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#191919", fontFamily: "Roboto, sans-serif" }}>
                {row.role}
            </span>

            <DeviceSwap oldDevice={row.oldDevice} newDevice={row.newDevice} />

            <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#191919", fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap" }}>
                {timeAgo(filter === "pending" ? row.requestedAt : row.decidedAt)}
            </span>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%" }}>
                <ActionButtons row={row} filter={filter} busy={busy} onDecide={onDecide} />
            </div>
        </div>

        {!isLast && (
            <div style={{ position: "absolute", left: fluid(24), right: fluid(24), bottom: 0, height: "1px", backgroundColor: COLORS.borderLight }} />
        )}
    </div>
));
TableRow.displayName = 'TableRow';

const GridCard = React.memo(({ row, filter, busy, onDecide }) => (
    <div style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        padding: fluid(22),
        boxSizing: "border-box",
        backgroundColor: "#FFFFFF",
        borderRadius: fluid(24),
        boxShadow: "4px 4px 10px 4px rgba(0,0,0,0.04)",
        gap: fluid(16),
    }}>
        <div style={{ display: "flex", alignItems: "center", gap: fluid(14), minWidth: 0 }}>
            <Avatar name={row.name} image={row.image} size={60} />
            <div style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontSize: fluid(18), fontWeight: 600, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                    {row.name}
                </span>
                <span style={{ display: "block", fontSize: fluid(15), fontWeight: 400, color: "#555555", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif", marginTop: fluid(2) }}>
                    {row.phone?.replace(/^\+91/, "")} · {row.role}
                </span>
            </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: fluid(6), padding: `${fluid(14)} ${fluid(16)}`, backgroundColor: "#FAFAFA", borderRadius: fluid(14) }}>
            <span style={{ fontSize: fluid(15), lineHeight: fluid(24), fontWeight: 400, color: "#222222", fontFamily: "Roboto, sans-serif" }}>
                <span style={{ fontWeight: 600, color: "#191919" }}>Old device — </span>{row.oldDevice}
            </span>
            <span style={{ fontSize: fluid(15), lineHeight: fluid(24), fontWeight: 400, color: "#222222", fontFamily: "Roboto, sans-serif" }}>
                <span style={{ fontWeight: 600, color: "#191919" }}>New device — </span>{row.newDevice}
            </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: fluid(15), fontWeight: 400, color: "#191919", fontFamily: "Roboto, sans-serif" }}>
                {timeAgo(filter === "pending" ? row.requestedAt : row.decidedAt)}
            </span>
            <ActionButtons row={row} filter={filter} busy={busy} onDecide={onDecide} />
        </div>
    </div>
));
GridCard.displayName = 'GridCard';

const DeviceApprovalRequests = React.memo(({
    isMobile = false,
    role = "student",
    onChanged,
    deviceApprovalRequests,
    getAllDeviceApprovalRequests,
    updateDeviceApprovalRequest,
    getDeviceApprovalRequestsCounts,
    user,
}) => {
    const isStaff = role === "staff";
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(!isStaff);
    const [viewMode, setViewMode] = useState("list");
    const [busyId, setBusyId] = useState(null);
    // { id, approvalStatus } of the request awaiting confirmation in the modal.
    const [pendingDecision, setPendingDecision] = useState(null);
    // Local, editable copy of MOCK_ROWS so APPROVE/REJECT can be demoed
    // without a server — see the comment on MOCK_ROWS above.
    const [mockRows, setMockRows] = useState(MOCK_ROWS);
    const [tabCounts, setTabCounts] = useState(null);

    const fetchCounts = useCallback(() => {
        if (isStaff) return;
        getDeviceApprovalRequestsCounts?.((counts) => {
            if (counts) {
                setTabCounts((prev) => ({
                    ...(prev || {}),
                    ...counts,
                }));
            }
        });
    }, [isStaff, getDeviceApprovalRequestsCounts]);

    const refetch = useCallback((status, callback) => {
        if (isStaff) {
            setLoading(false);
            callback?.();
            return;
        }
        setLoading(true);
        const targetStatus = status || filter;
        getAllDeviceApprovalRequests({ status: targetStatus }, (res) => {
            setLoading(false);
            if (res !== undefined) {
                const list = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
                setTabCounts((prev) => {
                    const base = prev ? { ...prev } : { pending: 0, approved: 0, rejected: 0 };
                    base[targetStatus] = list.length;
                    if (res?.counts) {
                        Object.assign(base, res.counts);
                    } else if (res?.meta?.counts) {
                        Object.assign(base, res.meta.counts);
                    }
                    return base;
                });
            }
            callback?.();
        });
        // getAllDeviceApprovalRequests only calls back on a real response —
        // with the server down that never fires, so this would otherwise
        // spin forever instead of falling back to MOCK_ROWS below.
        setTimeout(() => setLoading(false), 4000);
    }, [isStaff, getAllDeviceApprovalRequests, filter]);

    useEffect(() => {
        if (isStaff) {
            setLoading(false);
            return;
        }
        refetch(filter);
        fetchCounts();
    }, [isStaff, filter, refetch, fetchCounts]);

    const realRows = useMemo(() => {
        if (isStaff) return [];
        const list = Array.isArray(deviceApprovalRequests) ? deviceApprovalRequests : (deviceApprovalRequests?.data || []);
        return list.map(mapRecordToRow);
    }, [isStaff, deviceApprovalRequests]);

    const isApiSuccess = tabCounts !== null;
    const usingMock = !isStaff && !isApiSuccess && realRows.length === 0;
    const rows = useMemo(() => {
        if (isStaff) return [];
        return usingMock ? mockRows.filter((row) => row.approvalStatus === filter) : realRows;
    }, [isStaff, usingMock, mockRows, realRows, filter]);

    const counts = useMemo(() => {
        if (usingMock) {
            return {
                pending: mockRows.filter((row) => row.approvalStatus === "pending").length,
                approved: mockRows.filter((row) => row.approvalStatus === "approved").length,
                rejected: mockRows.filter((row) => row.approvalStatus === "rejected").length,
            };
        }
        return tabCounts;
    }, [usingMock, mockRows, tabCounts]);

    const handleFilterChange = (value) => {
        if (value === filter) return;
        setFilter(value);
        // Every tab switch opens in the table view.
        setViewMode("list");
    };

    // PATCH /user/device-approval-request { requestId, approvalStatus, approvedBy }
    // — identical payload to the old RegistrationRequestCard.handleButtonActions.
    const handleDecide = useCallback((id, approvalStatus) => {
        setPendingDecision({ id, approvalStatus });
    }, []);

    const handleConfirmDecision = useCallback(() => {
        if (!pendingDecision) return;
        const { id, approvalStatus } = pendingDecision;
        if (usingMock) {
            setMockRows((prev) => prev.map((row) => (
                row.id === id ? { ...row, approvalStatus, decidedAt: new Date().toISOString() } : row
            )));
            setPendingDecision(null);
            onChanged?.();
            return;
        }
        setBusyId(id);
        updateDeviceApprovalRequest({
            requestId: id,
            approvalStatus,
            approvedBy: user?.id,
        }, () => {
            setBusyId(null);
            setPendingDecision(null);
            refetch(filter, () => onChanged?.());
            fetchCounts();
        });
    }, [pendingDecision, usingMock, updateDeviceApprovalRequest, user, filter, refetch, fetchCounts, onChanged]);

    return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: fluid(14),
                paddingBottom: fluid(18),
                flexShrink: 0,
            }}>
                <div style={{ display: "flex", gap: fluid(8) }}>
                    {FILTER_CHIPS.map((chip) => {
                        const isSelected = filter === chip.value;
                        const count = counts ? counts[chip.value] : null;
                        const label = count !== null && count !== undefined
                            ? `${chip.label} (${count})`
                            : chip.label;
                        return (
                            <button
                                type="button"
                                key={chip.value}
                                onClick={() => handleFilterChange(chip.value)}
                                style={{
                                    padding: `${fluid(6)} ${fluid(16)}`,
                                    borderRadius: fluid(20),
                                    fontSize: fluid(16),
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    border: `1px solid ${isSelected ? COLORS.primary : "#D9D9D9"}`,
                                    backgroundColor: isSelected ? COLORS.primary : "#FFF",
                                    color: isSelected ? "#FFF" : "#636363",
                                    transition: "all 0.2s",
                                }}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={() => setViewMode((m) => (m === "list" ? "grid" : "list"))}
                    title={viewMode === "list" ? "Grid view" : "List view"}
                    style={{
                        width: fluid(40),
                        height: fluid(40),
                        borderRadius: fluid(8),
                        border: "none",
                        backgroundColor: "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: fluid(4),
                        cursor: "pointer",
                        flexShrink: 0,
                    }}
                >
                    <img src={viewMode === "list" ? GridIcon : ListIcon} alt={viewMode === "list" ? "Grid view" : "List view"} style={{ width: fluid(28), height: fluid(28) }} />
                </button>
            </div>

            {!loading && usingMock && (
                <div style={{ fontSize: fluid(12), fontStyle: "italic", color: COLORS.textLight, paddingBottom: fluid(10), flexShrink: 0 }}>
                    Showing sample data — the server is unreachable right now, so this isn't the live list.
                </div>
            )}

            <style>{`.device-requests-scroll::-webkit-scrollbar { display: none; }`}</style>

            <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
                {loading && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#FFFFFF", zIndex: 10 }}>
                        <Spin size="large" />
                    </div>
                )}

                {viewMode === "list" && (
                    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", backgroundColor: "#FFFFFF", borderRadius: fluid(16), border: `1px solid ${COLORS.borderLight}`, boxSizing: "border-box", overflow: "hidden" }}>
                        {/* Sticky, non-scrolling header — only the rows below scroll. */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: COLUMN_TEMPLATE,
                            gap: fluid(16),
                            padding: `${fluid(16)} ${fluid(24)}`,
                            backgroundColor: "#F4F4F4",
                            flexShrink: 0,
                        }}>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>{isStaff ? "Staff" : "Student"}</span>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Role</span>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Device change</span>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Requested</span>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Actions</span>
                        </div>

                        <div
                            className="device-requests-scroll"
                            style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none", msOverflowStyle: "none" }}
                        >
                            {rows.length === 0 ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: fluid(200), color: COLORS.textLight, fontSize: fluid(14) }}>
                                    There are currently no {filter} device change requests.
                                </div>
                            ) : (
                                rows.map((row, index) => (
                                    <TableRow
                                        key={row.id}
                                        row={row}
                                        filter={filter}
                                        busy={busyId === row.id}
                                        onDecide={handleDecide}
                                        isLast={index === rows.length - 1}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

                {viewMode === "grid" && (
                    <div
                        className="device-requests-scroll"
                        style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none", msOverflowStyle: "none" }}
                    >
                        {rows.length === 0 ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: fluid(200), color: COLORS.textLight, fontSize: fluid(14) }}>
                                There are currently no {filter} device change requests.
                            </div>
                        ) : (
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
                                gap: fluid(20),
                                paddingBottom: fluid(4),
                            }}>
                                {rows.map((row) => (
                                    <GridCard
                                        key={row.id}
                                        row={row}
                                        filter={filter}
                                        busy={busyId === row.id}
                                        onDecide={handleDecide}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <UserActionConfirmModal
                open={!!pendingDecision}
                action={pendingDecision?.approvalStatus === "approved" ? "deviceApprove" : "deviceReject"}
                loading={!!pendingDecision && busyId === pendingDecision.id}
                onCancel={() => setPendingDecision(null)}
                onConfirm={handleConfirmDecision}
            />
        </div>
    );
});

DeviceApprovalRequests.displayName = 'DeviceApprovalRequests';

const mapStateToProps = (state) => {
    const { deviceApprovalRequests } = state.userManagementV2;
    const { user } = state.session;
    return { deviceApprovalRequests, user };
};

const mapDispatchToProps = (dispatch) => ({
    getAllDeviceApprovalRequests: (params, callback) => dispatch(getAllDeviceApprovalRequests(params, callback)),
    updateDeviceApprovalRequest: (params, callback) => dispatch(updateDeviceApprovalRequest(params, callback)),
    getDeviceApprovalRequestsCounts: (callback) => dispatch(getDeviceApprovalRequestsCounts(callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceApprovalRequests);
